import React, { useEffect, useState } from 'react';
import { Lock } from 'lucide-react';
import SimplifyGameCard from './SimplifyGameCard';
import GameSearchDropdown from '../common/GameSearchDropdown';
import { GROUP_ICON, TIMER_ICON, HEART_ICON } from '../../constants/icons';
import { ENERGY_OPTIONS, FAMILIARITY_OPTIONS, TIME_OPTIONS, getFeelDisplayLabel } from '../../hooks/useSimplifyPreferences';
import './ModalComponents.css';
import '../../styles/popular-games-carousel.css';
import '../../styles/quickdecision.css';
import logger from '../../utils/logger';

// Default popular game IDs (fetched from DB on load, configurable by admins)
const DEFAULT_POPULAR_GAME_IDS = [230802, 30549, 178900, 822, 342942, 13];

const SimplifySearchPanel = ({
  selectedGames,
  _setSelectedGames,
  gameSearchQuery,
  setGameSearchQuery,
  gameSearchResults,
  _setGameSearchResults,
  showGameDropdown,
  setShowGameDropdown,
  isGameSearchLoading,
  _setIsGameSearchLoading,
  onSelectGame,
  showGameSelectionBanner,
  bannerMessage,
  onRemoveGame,
  onClearAll,
  onGetRecommendations,
  isSearching,
  onSearchGames,
  onToggleFamiliar,
  onFindInCollection,
  loadingCollection,
  ownedCount = 0,
  user,
  onBack,
  preferences,
  familiarity = 'any',
  setFamiliarity,
  playListCount = 0,
  onAddFromPlayList,
}) => {
  const isAnonymous = !user || !user.id;
  const [popularGames, setPopularGames] = useState([]);
  const [loadingPopular, setLoadingPopular] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [availabilityScope, setAvailabilityScope] = useState('extended'); // 'extended' or 'owned-only'
  const [onlineGamesCount, setOnlineGamesCount] = useState(0);

  // Fetch online games count on mount
  useEffect(() => {
    const fetchOnlineCount = async () => {
      try {
        const { API_BASE } = await import('../../config/api');
        const { httpRequest } = await import('../../utils/httpClient');
        const res = await httpRequest(`${API_BASE}/games/online-count`);
        if (res.ok) {
          const data = await res.json();
          setOnlineGamesCount(data.online_count || 0);
        }
      } catch (err) {
        logger.debug('Failed to fetch online games count:', err);
        setOnlineGamesCount(0);
      }
    };
    fetchOnlineCount();
  }, []);

  // Fetch popular games from admin config, then get full game details.
  // Deferred 200ms so the /collection/count request (which enables the button)
  // gets fetch priority over this cosmetic carousel load.
  useEffect(() => {
    const fetchPopularGames = async () => {
      try {
        setLoadingPopular(true);
        const { API_BASE } = await import('../../config/api');
        const { httpRequest } = await import('../../utils/httpClient');

        // First, fetch configurable popular game IDs from admin endpoint
        let popularIds = DEFAULT_POPULAR_GAME_IDS;
        try {
          const configRes = await httpRequest(`${API_BASE}/admin/popular-games`);
          if (configRes.ok) {
            const config = await configRes.json();
            if (config.game_ids && config.game_ids.length > 0) {
              popularIds = config.game_ids;
            }
          }
        } catch (err) {
          logger.debug('Using default popular games (admin config not available)');
        }

        // Fetch full game details for each ID
        const games = await Promise.all(
          popularIds.map(async (id) => {
            try {
              const res = await httpRequest(`${API_BASE}/games/${id}/details`);
              if (res.ok) {
                const data = await res.json();
                return {
                  id: data.id,
                  name: data.name,
                  thumbnail: data.thumbnail
                };
              }
            } catch (err) {
              logger.error(`Failed to fetch popular game ${id}:`, err);
            }
            return null;
          })
        );
        setPopularGames(games.filter(g => g !== null));
      } catch (err) {
        logger.error('Failed to load popular games:', err);
      } finally {
        setLoadingPopular(false);
      }
    };

    const timer = setTimeout(fetchPopularGames, 200);
    return () => clearTimeout(timer);
  }, []);

  // Trigger search when query changes
  useEffect(() => {
    if (onSearchGames) {
      onSearchGames(gameSearchQuery);
    }
  }, [gameSearchQuery, onSearchGames]);

  const preferenceBadgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    whiteSpace: 'nowrap',
    padding: '0.2rem 0.5rem',
    backgroundColor: 'var(--bg-primary, white)',
    border: '1px solid var(--border-color, #ddd)',
    borderRadius: '4px',
    color: 'var(--text-primary, #333)',
  };

  return (
    <div className="simplify-search-panel ssp-root">

      {/* Login Required Modal */}
      {showLoginModal && (
        <div className="login-modal-overlay" onClick={() => setShowLoginModal(false)}>
          <div className="login-modal-content" onClick={(e) => e.stopPropagation()}>
            <h4 className="login-modal-title">Login Required 🔒</h4>
            <p className="login-modal-message">
              Login to unlock the &quot;Find in My Collection&quot; feature and see games from your collection.
            </p>
            <div className="login-modal-buttons">
              <button
                className="login-modal-button primary"
                onClick={() => { setShowLoginModal(false); window.location.href = '/login'; }}
              >
                Go to Login
              </button>
              <button className="login-modal-button secondary" onClick={() => setShowLoginModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <div className="ssp-header">
        <h3 className="ssp-title">Game Selection</h3>
        <span className="ssp-count-badge">{selectedGames.length}</span>
      </div>

      {/* ── Preference Summary ── */}
      {preferences && (
        <div
          className="ssp-preference-summary"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.5rem',
            fontSize: '0.85rem',
            padding: '0.5rem 0.75rem',
            backgroundColor: 'var(--bg-secondary, #f5f5f5)',
            borderRadius: '8px',
            marginBottom: '1rem',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}
        >
          {preferences.players && (
            <span style={preferenceBadgeStyle}>
              {GROUP_ICON} {preferences.players} {preferences.players === 1 ? 'Player' : 'Players'}
            </span>
          )}
          {preferences.feel && (
            <span style={preferenceBadgeStyle}>
              🎯 {getFeelDisplayLabel(preferences.feel)} Interaction
            </span>
          )}
          {preferences.energy && (
            <span style={preferenceBadgeStyle}>
              ⚡ {ENERGY_OPTIONS.find((opt) => opt.value === preferences.energy)?.label || preferences.energy} Energy Level
            </span>
          )}
          {preferences.time && (
            <span style={preferenceBadgeStyle}>
              {TIMER_ICON} {TIME_OPTIONS.find((opt) => opt.value === preferences.time)?.label || preferences.time} Playtime
            </span>
          )}
          {preferences.familiarity && preferences.familiarity !== 'any' && (
            <span style={preferenceBadgeStyle}>
              {HEART_ICON} {FAMILIARITY_OPTIONS.find((opt) => opt.value === preferences.familiarity)?.label || preferences.familiarity}
            </span>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
            <button
              onClick={onBack}
              style={{
                padding: '0.2rem 0.5rem',
                fontSize: '0.8rem',
                backgroundColor: 'transparent',
                border: '1px solid var(--border-color, #ddd)',
                borderRadius: '4px',
                cursor: 'pointer',
                color: 'var(--text-secondary, #666)',
              }}
            >
              ← Back
            </button>
            <button
              onClick={onBack}
              style={{
                padding: '0.2rem 0.5rem',
                fontSize: '0.8rem',
                backgroundColor: 'transparent',
                border: '1px solid var(--border-color, #ddd)',
                borderRadius: '4px',
                cursor: 'pointer',
                color: 'var(--text-secondary, #666)',
              }}
            >
              Edit
            </button>
          </div>
        </div>
      )}

      {/* ── Two-column body ── */}
      <div className="ssp-columns">

        {/* LEFT — Add games */}
        <div className="ssp-col ssp-col--add">
          <h4 className="ssp-col-title">Add Games</h4>

          {/* Collection import section (locked for anonymous users) */}
          <div className={`ssp-import-section${isAnonymous ? ' ssp-import-section--locked' : ''}`}>
            <h5 className="ssp-section-header">Available games</h5>
            {isAnonymous && (
              <div
                className="ssp-import-lock-overlay"
                onClick={() => setShowLoginModal(true)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setShowLoginModal(true); }}
                role="button"
                tabIndex={0}
                aria-label="Login to use collection and playlist options"
              >
                <div className="ssp-import-lock" aria-hidden="true">
                  <span className="ssp-import-lock-message">Login to use these options</span>
                  <Lock size={20} />
                </div>
              </div>
            )}

            {/* Availability scope switch - compact, centered, above main CTA */}
            {!isAnonymous && (
              <div className="ssp-availability-switch" style={{ marginBottom: '0.5rem', textAlign: 'center' }}>
                <div
                  style={{
                    display: 'inline-flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'center',
                    maxWidth: '100%',
                    border: '1px solid var(--border-color, #ddd)',
                    borderRadius: '16px',
                    backgroundColor: 'var(--bg-secondary, #f8f8f8)',
                    padding: '1px',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setAvailabilityScope('extended');
                      setFamiliarity && setFamiliarity('any');
                    }}
                    style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '14px',
                      border: 'none',
                      backgroundColor: availabilityScope === 'extended' ? 'var(--primary-color, #1976d2)' : 'transparent',
                      color: availabilityScope === 'extended' ? 'white' : 'var(--text-primary, #333)',
                      fontSize: '0.75rem',
                      fontWeight: availabilityScope === 'extended' ? '500' : '400',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    + {onlineGamesCount} Online games
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAvailabilityScope('owned-only');
                      setFamiliarity && setFamiliarity('familiar');
                    }}
                    style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '14px',
                      border: 'none',
                      backgroundColor: availabilityScope === 'owned-only' ? 'var(--primary-color, #1976d2)' : 'transparent',
                      color: availabilityScope === 'owned-only' ? 'white' : 'var(--text-primary, #333)',
                      fontSize: '0.75rem',
                      fontWeight: availabilityScope === 'owned-only' ? '500' : '400',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Only my games
                  </button>
                </div>
              </div>
            )}

            <button
              className={`ssp-owned-btn${isAnonymous ? ' ssp-owned-btn--locked' : ''}`}
              onClick={() => {
                if (isAnonymous) { setShowLoginModal(true); return; }
                onFindInCollection && onFindInCollection();
              }}
              disabled={loadingCollection}
              title={isAnonymous ? 'Login to unlock' : 'Import from available games'}
            >
              {loadingCollection ? (
                <><span className="ssp-spin">⟳</span> Processing collection...</>
              ) : (
                <>{isAnonymous ? 'Find in available games' : `Find in available games (${availabilityScope === 'extended' ? ownedCount + onlineGamesCount : ownedCount})`}</>
              )}
            </button>
          </div>

          {/* Search and popular games section */}
          <div className="ssp-search-section">
            <h5 className="ssp-section-header">Search</h5>
            <button
              className={`ssp-playlist-btn${isAnonymous || playListCount === 0 ? ' ssp-playlist-btn--locked' : ''}`}
              onClick={() => {
                if (isAnonymous) { setShowLoginModal(true); return; }
                if (playListCount > 0 && onAddFromPlayList) {
                  onAddFromPlayList();
                }
              }}
              disabled={isAnonymous || playListCount === 0}
              title={isAnonymous ? 'Login to use Play List' : playListCount === 0 ? 'Your Play List is empty' : 'Add all games from your Play List'}
              data-testid="add-from-playlist-button"
            >
              Add from Play List {playListCount > 0 && !isAnonymous ? `(${playListCount})` : ''}
            </button>

            <span className="ssp-or">OR</span>

            {/* Search */}
            <div className="ssp-search-wrap">
              <GameSearchDropdown
                searchQuery={gameSearchQuery}
                onSearchChange={setGameSearchQuery}
                isVisible={showGameDropdown}
                onVisibilityChange={setShowGameDropdown}
                searchResults={gameSearchResults}
                isLoading={isGameSearchLoading}
                onGameSelect={onSelectGame}
                showImages={true}
                placeholder="Search for games..."
              />
            </div>

            {/* Popular Games */}
            <div className="ssp-popular">
              <h4 className="ssp-col-subtitle">Frequently added</h4>
              {loadingPopular ? (
                <p className="loading-text">Loading popular games...</p>
              ) : popularGames.length === 0 ? (
                <p className="no-games-text">Popular games not available.</p>
              ) : (
                <div className="popular-games-carousel">
                  {popularGames.map((game, index) => (
                    <div
                      className="popular-game-card"
                      key={index}
                      onClick={() => onSelectGame(game)}
                    >
                      <img
                        src={game.thumbnail}
                        alt={game.name}
                        className="popular-game-image"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <span className="popular-game-name">{game.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

      {/* RIGHT — Selected games */}
        <div className="ssp-col ssp-col--selected">
          <div className="ssp-selected-header">
            <h4 className="ssp-col-title">Selected Games ({selectedGames.length})</h4>
            {selectedGames.length > 0 && (
              <button onClick={onClearAll} className="clear-all-button">Clear All</button>
            )}
          </div>

          {selectedGames.length === 0 ? (
            <p className="no-games-text ssp-empty-hint">
              Search and add games on the left.
            </p>
          ) : (
            <div className={familiarity === 'any' ? 'sgc-list--compact' : ''}>
              {selectedGames.map(game => (
                <SimplifyGameCard
                  key={game.id}
                  game={game}
                  onRemove={onRemoveGame}
                  onToggleFamiliar={onToggleFamiliar}
                  hideFamiliarity={familiarity === 'any'}
                />
              ))}
            </div>
          )}

          {selectedGames.length > 0 && (
            <p className="ssp-add-more">+ Add more games from search or your Play List</p>
          )}

          <button
            onClick={onGetRecommendations}
            disabled={selectedGames.length === 0 || isSearching}
            className="calculate-button"
          >
            {isSearching ? (
              <><span className="ssp-spin">⟳</span> Getting recommendations...</>
            ) : (
              <>Next: Get Results {selectedGames.length === 0 ? '(add a game first)' : '→'}</>
            )}
          </button>
        </div>
      </div>

      {/* ── Progress ── */}
      <div className="spp-progress spp-progress--step2" aria-label="Step 2 of 3: Select games">
        <div className="spp-progress-bar">
          <div className="spp-progress-bar__fill" />
        </div>
        <span className="spp-progress-label">Step 2 of 3: Select games</span>
      </div>


      {/* Banner */}
      {showGameSelectionBanner && (
        <div className="alert-banner info" style={{ marginTop: '0.5rem' }}>
          {bannerMessage}
        </div>
      )}
    </div>
  );
};

export default SimplifySearchPanel;
